import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import apiConfig from '../../config/apiConfig.js';

interface Issue {
  id?: string;
  _id?: string;
  ticketId?: string;
  subject?: string;
  title?: string;
  description: string;
  status: 'Open' | 'In Progress' | 'Resolved';
  submittedOn?: string;
  createdAt?: string;
  createdDate?: string;
  replies?: any[];
  conversation?: Array<{
    _id?: string;
    sender: string;
    message: string;
    timestamp: string;
    attachments?: string[];
  }>;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

interface IssueForm {
  subject: string;
  description: string;
  attachments?: FileList | null | undefined;
}

// Default FAQ data as fallback
const defaultFaqData: FAQ[] = [
  {
    id: 'faq1',
    question: 'What is Gift from Heaven and how does it work?',
    answer: 'Gift from Heaven allows you to schedule and send gifts or heartfelt messages for the future — even posthumously or on specific life occasions.'
  },
  {
    id: 'faq2',
    question: 'How do I create and send a gift?',
    answer: 'Navigate to the gift creation section, select your gift type, add your message, choose delivery date, and recipient details.'
  },
  {
    id: 'faq3',
    question: 'Can I send a gift anonymously?',
    answer: 'Yes, you can choose to send gifts anonymously through our platform settings.'
  },
  {
    id: 'faq4',
    question: 'What types of gifts can I send?',
    answer: 'You can send digital messages, photos, videos, and coordinate physical gift deliveries through our platform.'
  },
  {
    id: 'faq5',
    question: 'How do legacy contacts work?',
    answer: 'Legacy contacts are trusted individuals who can manage your account and scheduled gifts in case of unforeseen circumstances.'
  },
  {
    id: 'faq6',
    question: 'Will my gift still be delivered if I\'m no longer around?',
    answer: 'Yes, our legacy system ensures your scheduled gifts and messages are delivered according to your wishes.'
  },
  {
    id: 'faq7',
    question: 'Can I edit a gift after I\'ve started creating it?',
    answer: 'Yes, you can save gifts as drafts and edit them until they are scheduled for delivery.'
  },
  {
    id: 'faq8',
    question: 'Can I schedule gifts for recurring events?',
    answer: 'Currently, each gift needs to be scheduled individually, but we\'re working on recurring gift features.'
  },
  {
    id: 'faq9',
    question: 'What happens if a recipient doesn\'t confirm the gift?',
    answer: 'We have follow-up procedures and will attempt multiple delivery methods to ensure your gift reaches its recipient.'
  },
  {
    id: 'faq10',
    question: 'How do I manage my subscription?',
    answer: 'You can view and manage your subscription details from your profile page under the subscription section.'
  }
];

export const useHelpSupportData = () => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>(defaultFaqData);
  const [loading, setLoading] = useState(true); // Main page loading (FAQ + Tickets)
  const [ticketsLoading, setTicketsLoading] = useState(false); // Individual tickets loading
  const [ticketDetailLoading, setTicketDetailLoading] = useState(false); // Ticket detail loading

  // Fetch tickets from API
  const fetchTickets = async (showIndividualLoader = false) => {
    if (showIndividualLoader) {
      setTicketsLoading(true);
    }

    // Add minimum loading time to ensure loader is visible
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 1500));

    try {
      const authToken = localStorage.getItem('authToken');
      const apiCall = axios.get(`${apiConfig.apiBaseURL}/support/tickets`, {
        headers: {
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });

      // Wait for both API call and minimum loading time
      const [response] = await Promise.all([apiCall, minLoadingTime]);

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setIssues(response.data.data);
      } else {
        setIssues([]);
      }
    } catch (error) {
      // Still wait for minimum loading time even on error
      await minLoadingTime;

      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
      setIssues([]);
    } finally {
      if (showIndividualLoader) {
        setTicketsLoading(false);
      }
    }
  };

  // Fetch FAQs from API
  const fetchFAQs = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`${apiConfig.apiBaseURL}/faq/get-all-faqs`, {
        headers: {
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Transform API response: map _id to id
        const transformedFaqs = response.data.data.map((faq: any) => ({
          id: faq._id,
          question: faq.question,
          answer: faq.answer
        }));
        setFaqs(transformedFaqs);
      } else if (response.data && Array.isArray(response.data)) {
        // Transform API response: map _id to id
        const transformedFaqs = response.data.map((faq: any) => ({
          id: faq._id,
          question: faq.question,
          answer: faq.answer
        }));
        setFaqs(transformedFaqs);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      // Keep using default FAQ data as fallback
    }
  };

  // Submit new ticket
  const submitTicket = async (values: IssueForm) => {
    try {
      const authToken = localStorage.getItem('authToken');

      const formData = new FormData();
      // API expects 'title' instead of 'subject'
      formData.append('title', values.subject); // Using 'title' field name for API
      formData.append('description', values.description); // Lowercase as expected by API

      // Only append attachments if files are provided
      if (values.attachments && values.attachments.length > 0) {
        Array.from(values.attachments).forEach((file) => {
          formData.append('attachments', file);
        });
      }
      // Note: Do not send attachments field if no files - backend should treat it as optional

      const response = await axios.post(
        `${apiConfig.apiBaseURL}/support/raise-ticket`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      if (response.data) {
        toast.success('Your issue has been submitted successfully!');

        // Refresh tickets list
        await fetchTickets();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error('Failed to submit your issue. Please try again.');
      return false;
    }
  };

  // Fetch ticket details
  const fetchTicketDetails = async (ticketId: string): Promise<Issue | null> => {
    setTicketDetailLoading(true);

    // Add minimum loading time to ensure loader is visible
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 1000));

    try {
      const authToken = localStorage.getItem('authToken');
      const apiCall = axios.get(`${apiConfig.apiBaseURL}/support/ticket/${ticketId}`, {
        headers: {
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });

      // Wait for both API call and minimum loading time
      const [response] = await Promise.all([apiCall, minLoadingTime]);

      if (response.data && response.data.data) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      // Still wait for minimum loading time even on error
      await minLoadingTime;

      console.error('Error fetching ticket details:', error);
      toast.error('Failed to load ticket details');
      return null;
    } finally {
      setTicketDetailLoading(false);
    }
  };

  // Submit reply to ticket
  const submitReply = async (ticketId: string, replyText: string, replyFiles: FileList | null): Promise<boolean> => {
    try {
      const authToken = localStorage.getItem('authToken');

      let requestData: FormData | object;
      let headers: Record<string, string> = {
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      };

      if (replyFiles && replyFiles.length > 0) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('message', replyText);

        Array.from(replyFiles).forEach((file) => {
          formData.append('attachments', file);
        });

        requestData = formData;
        headers['Content-Type'] = 'multipart/form-data';
      } else {
        // Use JSON for text-only replies
        requestData = { message: replyText };
        headers['Content-Type'] = 'application/json';
      }

      const response = await axios.post(
        `${apiConfig.apiBaseURL}/support/ticket/${ticketId}/reply`,
        requestData,
        { headers }
      );

      if (response.data) {
        toast.success('Reply submitted successfully!');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('Failed to submit reply. Please try again.');
      return false;
    }
  };

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        // Fetch both FAQ and tickets in parallel
        await Promise.all([
          fetchTickets(false), // Don't show individual loader
          fetchFAQs()
        ]);
      } catch (error) {
        console.error('Error initializing help support data:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  return {
    issues,
    faqs,
    loading, // Main page loading (FAQ + Tickets)
    ticketsLoading, // Individual tickets loading
    ticketDetailLoading, // Ticket detail loading
    fetchTickets,
    fetchFAQs,
    submitTicket,
    fetchTicketDetails,
    submitReply,
    setIssues,
    setFaqs
  };
};